import { useCallback, useEffect, useState } from 'react';
import { describeFolderDelete } from '../../../../utils/docOrganization';
import { getDocSheetOpen } from '../../../../utils/docSheetPresence';
import { isAtlasUnfiledId } from '../constants';

const TYPING_SEL =
  'input, textarea, select, [contenteditable="true"], .confirm-modal__backdrop, .doc-sheet';

/**
 * Delete selected Atlas folders: empty ones go immediately; otherwise confirm.
 */
export function useAtlasFolderDelete({
  useDemo,
  organization,
  selectedIds,
  setSelectedIds,
  setExpandedId,
  setCtx,
}) {
  const [pending, setPending] = useState(null);

  const clearAfterDelete = useCallback(() => {
    setSelectedIds(new Set());
    setExpandedId(null);
    setCtx(null);
  }, [setSelectedIds, setExpandedId, setCtx]);

  const requestDelete = useCallback(
    (folderIds) => {
      if (useDemo || !organization?.deleteFolders) return;
      const ids = (folderIds?.length ? folderIds : [...selectedIds]).filter(
        (id) => !isAtlasUnfiledId(id)
      );
      if (ids.length === 0) return;
      const info = describeFolderDelete(
        organization.folders,
        organization.docFolders,
        ids
      );
      if (info.folderIds.length === 0) return;
      setCtx(null);
      if (info.canSkipConfirm) {
        organization.deleteFolders(info.folderIds, { cascadeSubfolders: true });
        clearAfterDelete();
        return;
      }
      setPending(info);
    },
    [useDemo, organization, selectedIds, setCtx, clearAfterDelete]
  );

  const confirmDelete = useCallback(
    (cascade) => {
      if (!pending) return;
      organization.deleteFolders(pending.folderIds, {
        cascadeSubfolders: cascade,
      });
      setPending(null);
      clearAfterDelete();
    },
    [pending, organization, clearAfterDelete]
  );

  const cancelDelete = useCallback(() => setPending(null), []);

  useEffect(() => {
    if (useDemo) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (e.defaultPrevented || e.repeat) return;
      if (getDocSheetOpen()) return;
      if (document.querySelector('.docs-browse-popup')) return;
      if (pending) return;
      if (e.target?.closest?.(TYPING_SEL)) return;
      const ids = [...selectedIds].filter((id) => !isAtlasUnfiledId(id));
      if (ids.length === 0) return;
      e.preventDefault();
      requestDelete(ids);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [useDemo, pending, selectedIds, requestDelete]);

  return { pending, requestDelete, confirmDelete, cancelDelete };
}
